/**
 * Did the host app actually take the route, or did we just rewrite the address bar?
 *
 * The SDK navigates SPA-first: `history.pushState` then a synthetic `popstate`, so a
 * guided walkthrough keeps the app's state instead of reloading the world. The problem
 * is that the old success check compared `window.location` to the target — which
 * `pushState` had just satisfied. It could not fail. Observed on LeadRat (Angular):
 * the router could not match the URL, threw `NG04002`, reverted the view, and the SDK
 * still reported success. The agent announced "I've navigated to the email settings"
 * from the page it started on and carried on down the plan.
 *
 * Two signals actually distinguish the cases, and neither is the URL we set ourselves:
 *
 *   1. The router threw. A no-match surfaces as an unhandled promise rejection
 *      (Angular's NG04002 does exactly this), so listening for one across the attempt
 *      catches it without knowing which framework is present.
 *
 *   2. The router put the URL back. Routers restore the previous URL when navigation
 *      fails — Angular's `restoreHistory()` does this on error — so a URL that no
 *      longer matches the target is proof the app refused it.
 *
 * Known limit: a router that renders a catch-all 404 route holds the URL and throws
 * nothing, so this reports success. That is a wrong plan URL rather than a broken
 * navigation, and no generic signal separates "rendered their 404" from "rendered the
 * page" without app-specific knowledge.
 */

/** How long to let the router settle before judging. */
export const SPA_SETTLE_MS = 600;

/**
 * How long to wait for a fallback full page load to tear this context down before
 * concluding it will not. A load that is going to happen has unloaded us well inside
 * this; anything still running afterwards means it was blocked (cancelled
 * beforeunload, Content-Disposition: attachment) and must be reported as a failure
 * rather than left to a pagehide handler to claim as a success.
 */
export const FULL_LOAD_GRACE_MS = 3000;

export type NavigationTarget =
  | { ok: true; url: string; hostRewritten: boolean }
  | { ok: false; error: string };

/**
 * Turn a plan's NAVIGATE target into a URL on the origin the SDK is actually running on.
 *
 * A plan's stored URL carries the host it was RECORDED on, which is not where it will
 * be replayed. LeadRat gives every customer their own subdomain: a plan recorded on
 * nexus.leadrat.com would send acme.leadrat.com's user to nexus, which the old
 * same-origin guard correctly refused — so the step failed for every customer except
 * the one it was recorded against. The host in the plan is an artifact of recording,
 * not an instruction. Only the path is meaningful.
 *
 * So the host is always the current one. Two consequences worth being explicit about:
 *
 *   - This is STRICTLY safer than the old check, not a relaxation. Previously a plan
 *     naming another host was rejected; now it cannot navigate off-origin at all,
 *     because the origin is never taken from the plan. A plan can still choose a path,
 *     which is the same power its CLICK actions already have.
 *
 *   - It deliberately does NOT try to decide whether two hosts are "the same app".
 *     That means comparing registrable domains, which needs the public suffix list to
 *     do correctly — the naive last-two-labels version treats foo.co.uk and bar.co.uk
 *     as the same site. There is no reason to take that risk when the answer is always
 *     "use the current origin".
 *
 * Relative targets are anchored at the ORIGIN ROOT rather than the current URL. With
 * the current URL as base, a stored target of "global-config/email" resolves against
 * the page you happen to be on — /dashboard/stats becomes
 * /dashboard/global-config/email. Plan paths are absolute locations in the app, not
 * offsets from wherever the walkthrough got to.
 *
 * Known limit: this assumes the tenant lives in the HOST. A product that puts the
 * tenant in the path (/acme/global-config/email) needs a per-site path template, which
 * this does not attempt.
 */
export function resolveNavigationTarget(
  rawTarget: string,
  currentHref: string,
): NavigationTarget {
  let current: URL;
  try {
    current = new URL(currentHref);
  } catch {
    return { ok: false, error: 'current location could not be parsed' };
  }

  let parsed: URL;
  try {
    // Absolute targets parse on their own; relative ones anchor at the origin root.
    parsed = new URL(rawTarget, current.origin);
  } catch {
    return { ok: false, error: 'invalid URL' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    // Checked on the RAW target: javascript: and data: must never survive to the
    // recomposition below.
    return { ok: false, error: `disallowed URL scheme "${parsed.protocol}"` };
  }

  const hostRewritten = parsed.origin !== current.origin;
  return {
    ok: true,
    url: `${current.origin}${parsed.pathname}${parsed.search}${parsed.hash}`,
    hostRewritten,
  };
}

/**
 * The browser operations this needs, injected so the decision logic is testable
 * without a DOM (client tests run in node with no jsdom).
 */
export interface SpaNavigationAdapter {
  currentUrl(): string;
  pushUrl(url: string): void;
  restoreUrl(url: string): void;
  /**
   * Tell the router the URL changed. `hashChanged` matters: hash-based routers
   * (React Router's HashRouter and friends) listen for `hashchange`, not `popstate`,
   * so a popstate-only notify leaves them on the old view while the URL reads as the
   * target — which this module would then score as accepted.
   */
  notifyRouter(options: { hashChanged: boolean }): void;
  /** Subscribe to unhandled rejections; returns an unsubscribe function. */
  onUnhandledRejection(handler: (reason: unknown) => void): () => void;
  wait(ms: number): Promise<void>;
}

export interface SpaNavigationResult {
  accepted: boolean;
  reason: string;
}

/**
 * The real browser binding for the adapter above.
 *
 * This is the one part of the module that touches the DOM, so it is the one part the
 * node tests cannot exercise — everything that makes a DECISION lives in the pure
 * functions. It exists as a factory rather than an inline object literal because there
 * are two navigate call sites (the SDK's action handler and ActionExecutor), and a
 * second hand-rolled adapter is exactly how the two implementations drifted apart in
 * the first place: one of them dispatched `hashchange` and the other did not.
 */
export function createBrowserNavigationAdapter(): SpaNavigationAdapter {
  // Captured at push time: by the time notifyRouter runs, window.location is already
  // the new URL, so oldURL for the hashchange event has to be remembered here.
  let urlBeforePush = '';

  return {
    currentUrl: () => window.location.href,
    pushUrl: (url) => {
      urlBeforePush = window.location.href;
      const u = new URL(url);
      history.pushState(null, '', u.pathname + u.search + u.hash);
    },
    restoreUrl: (url) => {
      const u = new URL(url);
      history.replaceState(null, '', u.pathname + u.search + u.hash);
    },
    notifyRouter: ({ hashChanged }) => {
      window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
      if (hashChanged) {
        window.dispatchEvent(
          new HashChangeEvent('hashchange', {
            oldURL: urlBeforePush,
            newURL: window.location.href,
          }),
        );
      }
    },
    onUnhandledRejection: (handler) => {
      const listener = (event: PromiseRejectionEvent) => handler(event.reason);
      window.addEventListener('unhandledrejection', listener);
      return () => window.removeEventListener('unhandledrejection', listener);
    },
    wait: (ms) => new Promise((r) => setTimeout(r, ms)),
  };
}

/**
 * Rejections that plausibly came from a router refusing a route.
 *
 * A bare "any unhandled rejection means refusal" is far too broad. Real pages reject
 * promises constantly — an analytics beacon, an aborted fetch, one of the SDK's own —
 * and treating those as a refusal tears down a navigation the router ACCEPTED,
 * forcing a full reload that discards the app state this path exists to preserve and
 * takes the live WebRTC session with it. On a busy SPA that would quietly negate the
 * whole SPA-first path.
 *
 * So the rejection is only corroborating evidence, and only when it looks like
 * routing. The URL is the primary signal.
 */
const ROUTER_ERROR_PATTERNS: RegExp[] = [
  /NG04002/i, // Angular: "Cannot match any routes"
  /cannot match any routes/i,
  /no route matched/i,
  /route .*not found/i,
  /navigation (?:cancelled|canceled|aborted)/i,
  /no match(?:ing)? route/i,
];

export function looksLikeRouterError(reason: unknown): boolean {
  const text =
    reason instanceof Error
      ? `${reason.name}: ${reason.message}`
      : typeof reason === 'string'
        ? reason
        : (() => {
            try {
              return JSON.stringify(reason) ?? '';
            } catch {
              return '';
            }
          })();
  return ROUTER_ERROR_PATTERNS.some((p) => p.test(text));
}

/** Same page, ignoring a single trailing slash. */
export function sameLocation(a: string, b: string): boolean {
  const norm = (raw: string): string | null => {
    try {
      const u = new URL(raw);
      const path =
        u.pathname.length > 1 && u.pathname.endsWith('/')
          ? u.pathname.slice(0, -1)
          : u.pathname;
      return `${u.origin}${path}${u.search}${u.hash}`;
    } catch {
      return null;
    }
  };
  const left = norm(a);
  const right = norm(b);
  if (left === null || right === null) return a === b;
  return left === right;
}

/**
 * Attempt an SPA navigation and report whether the app took it.
 *
 * On refusal the caller should fall back to a full page load. The URL is restored
 * first when the router did not restore it itself, so history is not left with an
 * entry pointing at a route the app rejected.
 */
export async function attemptSpaNavigation(
  adapter: SpaNavigationAdapter,
  targetUrl: string,
  settleMs: number = SPA_SETTLE_MS,
): Promise<SpaNavigationResult> {
  const beforeUrl = adapter.currentUrl();

  if (sameLocation(beforeUrl, targetUrl)) {
    return { accepted: true, reason: 'already on the target URL' };
  }

  let routerError: string | null = null;
  const unsubscribe = adapter.onUnhandledRejection((reason) => {
    if (routerError === null && looksLikeRouterError(reason)) {
      routerError =
        reason instanceof Error ? reason.message : String(reason ?? 'unknown');
    }
  });

  try {
    const hashChanged = hashOf(targetUrl) !== hashOf(beforeUrl);
    adapter.pushUrl(targetUrl);
    adapter.notifyRouter({ hashChanged });
    await adapter.wait(settleMs);

    // The URL is checked FIRST and is decisive. If the router held it, the app took
    // the route — an unrelated rejection during the settle window must not undo that.
    if (!sameLocation(adapter.currentUrl(), targetUrl)) {
      // The router refused. Usually it restored the previous URL, but it may also
      // have sent us somewhere else entirely — a guard redirecting to /login, or a
      // catch-all 404 route. Anywhere that is not where we started has to be put
      // back, or the fallback full load runs from a page we never meant to be on and
      // history keeps an entry nobody asked for.
      const wentBack = sameLocation(adapter.currentUrl(), beforeUrl);
      if (!wentBack) adapter.restoreUrl(beforeUrl);

      // Worth distinguishing when reading a log: "reverted" is a plain refusal,
      // "redirected" usually means a route guard sent the user somewhere (a login
      // page, a catch-all 404) and points at a different fix.
      return {
        accepted: false,
        reason: routerError
          ? `the app’s router rejected the route (${routerError})`
          : wentBack
            ? 'the app’s router reverted the URL'
            : 'the app’s router redirected somewhere other than the target',
      };
    }

    if (routerError) {
      // URL held, but the router raised a routing error — not every router restores
      // history on failure. Put the URL back so the fallback starts from where we were.
      adapter.restoreUrl(beforeUrl);
      return {
        accepted: false,
        reason: `the app’s router rejected the route (${routerError})`,
      };
    }

    return { accepted: true, reason: 'the app’s router accepted the route' };
  } finally {
    unsubscribe();
  }
}

function hashOf(raw: string): string {
  try {
    return new URL(raw).hash;
  } catch {
    return '';
  }
}
